import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ImageIcon, Download, CheckCircle, AlertCircle } from "lucide-react";
import { convertToWebP, downloadWebP } from "@/utils/webpConverter";
import { useToast } from "@/hooks/use-toast";

interface ImageFile {
  name: string;
  size: number;
  path: string;
  converted?: boolean;
  webpSize?: number;
}

const IMAGE_FILES: ImageFile[] = [
  { name: "1d875431-108a-485d-a4e3-e27286786183.png", size: 0, path: "/lovable-uploads/1d875431-108a-485d-a4e3-e27286786183.png" },
  { name: "3aef1e6b-6e35-4249-905f-4d58c0135648.png", size: 0, path: "/lovable-uploads/3aef1e6b-6e35-4249-905f-4d58c0135648.png" },
  { name: "6f79c514-8a5e-451f-a5ed-e876d25c53e0.png", size: 0, path: "/lovable-uploads/6f79c514-8a5e-451f-a5ed-e876d25c53e0.png" },
  { name: "9610763a-5722-43d0-9058-e6e750bcd507.png", size: 0, path: "/lovable-uploads/9610763a-5722-43d0-9058-e6e750bcd507.png" },
  { name: "9de645e8-94d4-4ee0-a8d6-1114b3ea741b.png", size: 0, path: "/lovable-uploads/9de645e8-94d4-4ee0-a8d6-1114b3ea741b.png" },
  { name: "e1b90743-1060-4e0b-acf6-106bbabade35.png", size: 0, path: "/lovable-uploads/e1b90743-1060-4e0b-acf6-106bbabade35.png" }
];

export const ImageOptimizationSection = () => {
  const [images, setImages] = useState<ImageFile[]>(IMAGE_FILES);
  const [converting, setConverting] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();

  const convertSingleImage = async (image: ImageFile) => {
    try {
      const response = await fetch(image.path);
      const blob = await response.blob();
      const file = new File([blob], image.name, { type: blob.type });
      
      const webpBlob = await convertToWebP(file, 0.8);
      downloadWebP(webpBlob, image.name);
      
      setImages(prev => prev.map(img => 
        img.name === image.name 
          ? { ...img, converted: true, webpSize: webpBlob.size }
          : img
      ));

      toast({
        title: "Նկարը փոխարկվեց",
        description: `${image.name} WebP ֆորմատով ներբեռնվեց`,
      });
    } catch (error) {
      toast({
        title: "Սխալ",
        description: `${image.name} փոխարկման ժամանակ սխալ`,
        variant: "destructive"
      });
    }
  };

  const convertAllImages = async () => {
    setConverting(true);
    setProgress(0);

    for (let i = 0; i < images.length; i++) {
      try {
        await convertSingleImage(images[i]);
        setProgress(((i + 1) / images.length) * 100);
      } catch (error) {
        console.error(`Error converting ${images[i].name}:`, error);
      }
    }

    setConverting(false);
    toast({
      title: "Բոլոր նկարները փոխարկվեցին",
      description: "WebP նկարները ներբեռնվեցին",
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return 'N/A';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getSavingsPercentage = (original: number, webp: number) => {
    if (original === 0 || webp === 0) return 0;
    return Math.round(((original - webp) / original) * 100);
  };

  return (
    <Card className="border-border/70 bg-card/80">
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          Նկարների WebP օպտիմիզացիա
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 px-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Փոխարկել PNG/JPG նկարները WebP ֆորմատի՝ արագագործության բարելավման համար
          </p>
          <Button 
            onClick={convertAllImages}
            disabled={converting}
            className="min-h-11 w-full bg-primary hover:bg-primary/90 sm:w-auto"
          >
            {converting ? "Փոխարկվում է..." : "Փոխարկել բոլորը"}
          </Button>
        </div>

        {converting && (
          <div className="space-y-2">
            <Progress value={progress} className="h-2" />
            <p className="text-sm text-center text-muted-foreground">
              {Math.round(progress)}% ավարտված
            </p>
          </div>
        )}

        <div className="grid gap-3">
          {images.map((image) => (
            <div key={image.name} className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <img 
                  src={image.path} 
                  alt={image.name}
                  className="w-12 h-12 object-cover rounded"
                />
                <div className="min-w-0">
                  <p className="break-all text-sm font-medium sm:break-normal">{image.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(image.size)}
                    {image.webpSize && (
                      <span className="ml-2 text-green-600">
                        → {formatFileSize(image.webpSize)} 
                        ({getSavingsPercentage(image.size, image.webpSize)}% խնայում)
                      </span>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="flex w-full items-center gap-2 sm:w-auto">
                {image.converted ? (
                  <Badge variant="secondary" className="bg-green-500/10 text-green-600 dark:text-primary">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Փոխարկված
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => convertSingleImage(image)}
                    disabled={converting}
                    className="min-h-11 w-full sm:w-auto"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Փոխարկել
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-border/70 bg-primary/10 p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div className="text-sm">
              <p className="mb-1 font-medium text-primary">Ուշադրություն</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• WebP նկարները 25-50% փոքր են PNG/JPG-ից</li>
                <li>• Փոխարկված նկարները ներբեռնվելու են ձեր համակարգչում</li>
                <li>• Ձեռքով փոխարինել հին նկարները նորերով</li>
                <li>• Արդիացնել manifest.json և այլ ֆայլերի հղումները</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};